import * as React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import LoginForm from "./components/LoginForm/LoginForm";
import { RequireAuth } from "./components/requiredAuth";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";

import MpinInput from "./components/MPinInput/mpinInput";
import MpinSet from "./components/MPinInput/MpinSet";
import ForgotMpin from "./components/MPinInput/ForgetMpin/forgetMpin";
import { initRudder } from "./analytics/rudder";
import LoginOtp from "./components/LoginForm/loginOTPVerify";
import LoginWithQr from "./components/LoginForm/loginWithQr";
import SetTotp from "./components/LoginForm/TOTP/setTotp";
import EnableTotp from "./components/LoginForm/TOTP/EnableTotp";
import ResponseHandle from "./components/ResponseHandle/ResponseHandle";
import AuthStore from "./components/AuthStore/authStore";
import { t } from "./utils/appString";
import Approot from "./components/home";
import { InitOtel } from "./services/otel/init";
import TraceProvider from "./services/otel/instrumentation";
import ConsoleLogger from "./services/otel/consoleLogger";
import Redirection from "./components/Redirection";

declare global {
  interface Window {
    tvWidget: {
      showNoticeDialog: (options: {
        title: string;
        body: string;
        callback: () => void;
      }) => void;
      showConfirmDialog: (options: {
        title: string;
        body: string;
        callback: (param: boolean) => void;
      }) => void;
    };
  }
}

// RudderStack is the primary analytics SDK — initialise it as early as possible.
initRudder();

interface JWTPayload {
  exp: number;
  iat: number;
  iss: string;
  sub: string;
  [key: string]: any;
}

const decodeJWTPayload = (token: string): JWTPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.log("Error decoding JWT:", error);
    return null;
  }
};

// Validate refresh token
const isRefreshTokenValid = (): boolean => {
  try {
    const persistObject = localStorage.getItem("PERSIST_OBJECT");
    const useRefreshFlag = localStorage.getItem("useRefresh");

    if (!persistObject || !useRefreshFlag) {
      return false;
    }

    const parsed = JSON.parse(persistObject);
    const refreshExpiresAt = new Date(parsed.refresh_expires_at);

    if (!refreshExpiresAt) {
      clearInvalidRefreshData();
      return false;
    }

    // Check if token is expired (with 5 minute buffer)
    const now = new Date();

    if (now >= refreshExpiresAt) {
      clearInvalidRefreshData();
      return false;
    }

    return true;

  } catch (error) {
    clearInvalidRefreshData();
    return false;
  }
};

// Clear invalid refresh token data
const clearInvalidRefreshData = () => {
  localStorage.removeItem("PERSIST_OBJECT");
  localStorage.removeItem("useRefresh");
  localStorage.removeItem("AUTH_LOGIN");
};

// Get authentication status with validation
const getValidatedAuthStatus = () => {
  const hasValidRefresh = isRefreshTokenValid();
  let hasValidSSO = false;
  const authLogin = localStorage.getItem("AUTH_LOGIN");
  if (authLogin) {
    try {
      const parsed = JSON.parse(authLogin);
      const ssoToken = parsed.ssoToken;

      if (ssoToken) {
        const payload = decodeJWTPayload(ssoToken);
        if (payload) {
          const now = Math.floor(Date.now() / 1000);
          if (now < payload.exp) {
            hasValidSSO = true;
          } else {
            localStorage.removeItem("AUTH_LOGIN");
          }
        } else {
          localStorage.removeItem("AUTH_LOGIN");
        }
      }
    } catch (error) {
      localStorage.removeItem("AUTH_LOGIN");
    }
  }

  const hasAnyValidAuth = hasValidRefresh || hasValidSSO;

  return {
    hasValidRefresh,
    hasValidSSO,
    hasAnyValidAuth
  };
};

const AppRoutes: React.FC = () => {
  InitOtel({
    env: process.env.REACT_APP_ENV || "dev",
    name: "web-auth",
    version: `${process.env.REACT_APP_COMMIT_VERSION}`,
  });
  const navigate = useNavigate();
  const [hasCheckedInitial, setHasCheckedInitial] = React.useState(false);

  React.useEffect(() => {
    if (hasCheckedInitial) {
      return;
    }

    const currentPath = window.location.pathname;

    // Persist a ChatGPT OAuth authorize handoff as early as possible — before any
    // routing decision below can send a returning (already-authenticated) user
    // straight to /MPin, bypassing the login screen that normally captures these
    // params. Scoped to the OAuth case (client_id + redirect_uri) so no other
    // flow is affected.
    const initialSearch = new URLSearchParams(window.location.search);
    if (initialSearch.get("client_id") && initialSearch.get("redirect_uri")) {
      localStorage.setItem("Q_PARAM_ALL", JSON.stringify(initialSearch.toString()));
    }

    // Clear MPIN flags on app load for security
    sessionStorage.removeItem("COMING_FROM_MPIN");
    sessionStorage.removeItem("MPIN_NAVIGATION_SUCCESS");
    sessionStorage.removeItem("MPIN_AUTH_SESSION");
    sessionStorage.removeItem("MPIN_AUTHENTICATED");
    sessionStorage.removeItem("MPIN_COMPLETED");

    // Validate all tokens before making routing decisions
    const authStatus = getValidatedAuthStatus();

    setHasCheckedInitial(true);

    if (currentPath === "/apps") {
      if (!authStatus.hasAnyValidAuth) {
        navigate("/", { replace: true });
        return;
      }

      navigate("/MPin", { replace: true, state: { returnTo: "/apps" } });
      return;
    }
    const params = localStorage.getItem("Q_PARAM_ALL");
    const applicationId = params?.split("=");
    let appId: string = ""
    if (applicationId) {
      appId = applicationId[1]
    }
    if (appId && appId.startsWith("dev_")) {
      navigate("/", { replace: true });
      return
    }
    if (currentPath === "/" || currentPath === "/login") {
      if (authStatus.hasAnyValidAuth) {
        navigate("/MPin", { replace: true });
        return;
      }
    }

    if (currentPath === "/MPin") {
      if (!authStatus.hasAnyValidAuth) {
        navigate("/", { replace: true });
        return;
      }
    }

  }, [navigate, hasCheckedInitial]);

  return (
    <TraceProvider>
      <ConsoleLogger />
      <Routes>
        <Route path="/" element={<LoginForm />} />
        <Route path="/qrLogin" element={<LoginWithQr />} />
        <Route path="/login" element={<LoginForm />} />
        <Route path="/loginOTPVerify" element={<LoginOtp />} />
        <Route path="/setTOtp" element={<SetTotp />} />
        <Route path="/EnableTOtp" element={<EnableTotp />} />
        <Route path="/authStore" element={<AuthStore />} />

        <Route element={<RequireAuth />}>
          <Route path="/apps" element={<Approot />} />
        </Route>

        <Route path="/mpin-redirection" element={<Redirection />} />
        <Route path="/MPin" element={<MpinInput />} />
        <Route path="/ResetMPin" element={<ForgotMpin />} />
        <Route path="/MPinSet" element={<MpinSet />} />

        <Route path="*" element={<h1>Error 404 Page not Found !!</h1>} />
        <Route element={<ResponseHandle />} />
      </Routes>
    </TraceProvider>
  );
};

const App: React.FC = () => {
  return (
    <div id="themeManage">
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick={true}
        rtl={false}
        pauseOnHover={true}
      />

      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
};

export default App;